import { useHttp } from '@common/composables/useHttp';
import { notifyError } from '@common/utils/notification';
import { debounce } from 'lodash-es';
import qs from 'qs';
import { computed, readonly, ref, toRaw, watch } from 'vue';
const unreadNotificationsCount = ref(0);
/**
 * 🔔 Notifications Panel, tend to encapsulate the logic of the notifications panel and its shared state across the app
 */
export function useNotificationsPanel() {
  const pagination = ref({ currentPage: 1, lastPage: 1 });
  const filterUnreadOnly = ref(false);
  const loading = ref(false);
  const notifications = ref([]);

  const http = useHttp();

  function goToNextPage() {
    if (pagination.value.currentPage < pagination.value.lastPage) {
      pagination.value.currentPage++;
    }
  }

  const reachedLastPage = computed(
    () => pagination.value.currentPage === pagination.value.lastPage
  );

  async function getNextPage() {
    goToNextPage();
    await getAllNotifications(filterUnreadOnly.value ? { unread: true } : undefined);
  }

  function resetPagination() {
    pagination.value.currentPage = 1;
    pagination.value.lastPage = 1;
  }

  async function getAllNotifications() {
    try {
      const query = {
        ...(filterUnreadOnly.value && { filter: { unread: true } }),
        page: pagination.value.currentPage,
        sort: '-created_at',
      };
      const { data, meta } = await fetchNotifications(query);

      notifications.value =
        pagination.value.currentPage === 1 ? data : [...notifications.value, ...data];

      pagination.value.lastPage = meta.last_page;
    } finally {
      loading.value = false;
    }
  }

  async function getUnreadNotificationsCount() {
    const data = await fetchNotifications({
      filter: {
        unread: true,
      },
      per_page: 1,
    });

    unreadNotificationsCount.value = data.meta.total;
  }

  async function fetchNotifications(queryParameters = {}) {
    try {
      loading.value = true;
      const query = qs.stringify(queryParameters);
      const response = await http.get(`/notifications?${query}`);
      return response.data;
    } finally {
      loading.value = false;
    }
  }

  async function markNotificationAsRead(notificationId) {
    try {
      loading.value = true;
      await http.post(`/notifications/${notificationId}/mark_as_read`);
      const notification = notifications.value.find((n) => n.id === notificationId);
      if (notification && !notification.read_at) {
        notification.read_at = new Date();
        unreadNotificationsCount.value--;
      }
    } finally {
      loading.value = false;
    }
  }

  function markAllNotificationsAsRead() {
    const clonedNotifications = toRaw(notifications.value);
    const clonedUnreadNotificationsCount = toRaw(unreadNotificationsCount.value);

    try {
      http.post('/notifications/mark_all_as_read');
      // minimizing the number of iterations that has to be done
      // also ensuring that the newly updated array is mirroring the database
      if (filterUnreadOnly.value) {
        notifications.value = []; // if the filter is unread only, the accurate view is an empty array after marking all as read
        unreadNotificationsCount.value = 0;
      } else {
        notifications.value.forEach((notification) => {
          notification.read_at = new Date();
        });
        unreadNotificationsCount.value = 0;
      }
    } catch {
      notifications.value = clonedNotifications;
      unreadNotificationsCount.value = clonedUnreadNotificationsCount;
      notifyError('Failed to mark all notifications as read');
    }
  }

  watch(
    filterUnreadOnly,
    debounce(() => {
      resetPagination();
      getAllNotifications();
    }, 300)
  );

  return {
    filterUnreadOnly,
    getAllNotifications,
    getNextPage,
    getUnreadNotificationsCount,
    loading,
    markAllNotificationsAsRead,
    markNotificationAsRead,
    notifications: readonly(notifications),
    reachedLastPage,
    unreadNotificationsCount: readonly(unreadNotificationsCount),
  };
}
